import type { LucideIcon } from 'lucide-react';
import { File as FileIcon, FileImage, FileSpreadsheet, FileText, Loader2, X } from 'lucide-react';
import type { ThreadMessageAttachment } from '@/types';

/** Match object-update pills in chat-message (rounded-full bg-muted px-2.5 py-1). */
export const ATTACHMENT_CHIP_CLASS =
  'inline-flex max-w-[14rem] items-center gap-1 rounded-full bg-muted px-2.5 py-1 text-xs text-muted-foreground';

export function getAttachmentIconForMime(mime: string | undefined): LucideIcon {
  const m = (mime || '').toLowerCase();
  if (m.startsWith('image/')) return FileImage;
  if (m.includes('pdf')) return FileText;
  if (m.includes('csv') || m.includes('spreadsheet') || m.endsWith('sheet')) return FileSpreadsheet;
  return FileIcon;
}

/**
 * Staged upload chip: icon/spinner, name, failure marker, optional remove button.
 * Shared by the composer shell (pending uploads) and any staged-attachment surfaces.
 */
export function StagedAttachmentChip({
  attachment,
  onRemove,
}: {
  attachment: ThreadMessageAttachment;
  onRemove?: (id: string) => void;
}) {
  const Icon = getAttachmentIconForMime(attachment.mime_type);
  const showThumbnail = Boolean(attachment.previewUrl);
  return (
    <span
      className={showThumbnail ? `${ATTACHMENT_CHIP_CLASS} py-0.5 pl-0.5` : ATTACHMENT_CHIP_CLASS}
      title={attachment.original_name}
    >
      {showThumbnail ? (
        <span className="relative size-6 shrink-0 overflow-hidden rounded-full">
          <img
            src={attachment.previewUrl}
            alt=""
            className="size-full object-cover"
            draggable={false}
          />
          {attachment.isUploading ? (
            <span className="absolute inset-0 flex items-center justify-center bg-background/60">
              <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
            </span>
          ) : null}
        </span>
      ) : attachment.isUploading ? (
        <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin" aria-hidden />
      ) : (
        <Icon className="h-3.5 w-3.5 shrink-0" aria-hidden />
      )}
      <span className="truncate">{attachment.original_name}</span>
      {attachment.status === 'failed' && attachment.error ? (
        <span className="text-destructive" title={attachment.error}>
          !
        </span>
      ) : null}
      {onRemove ? (
        <button
          type="button"
          className="ml-0.5 rounded p-0.5 hover:bg-background/80"
          aria-label={`Remove ${attachment.original_name}`}
          onClick={() => onRemove(attachment.id)}
        >
          <X className="h-3.5 w-3.5" />
        </button>
      ) : null}
    </span>
  );
}
